import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Phone, User, FileText, BookOpen, Lock, Edit2, Check, X, Calendar, Clock } from 'lucide-react';
import api from '../../services/api';
import { getAvatarUrl } from '../../utils/avatar';
import { useConfirmation } from '../../context/ConfirmationContext';

export default function AdminStudentDetail() {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [student, setStudent] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [attendance, setAttendance] = useState([]);
  const [marks, setMarks] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const { showConfirmation } = useConfirmation();

  const formatDateTime = (value) => {
    if (!value) return 'Never';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Never';
    return date.toLocaleString();
  };

  useEffect(() => { loadData(); }, [studentId]);

  const loadData = async () => {
    try {
      const res = await api.get(`/admin/students/${studentId}`);
      if (res.data.success) {
        const data = res.data.data || {};
        const s = data.student || null;
        if (s) {
          setStudent(s);
          setFormData({
            full_name: s.full_name || '',
            email: s.email || '',
            phone_no: (s.phone_number ?? s.phone_no) || '',
            student_id_number: s.student_id_number || '',
            grade_id: s.grade_id || ''
          });
          setAttendance(data.attendance || []);
          setMarks(data.marks || []);
        } else {
          setError('Student not found');
        }
      } else {
        setError('Student not found');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load student details');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    try {
      setError('');
      
      // Validation
      if (!formData.full_name.trim()) {
        setError('❌ Full name is required');
        return;
      }
      if (!formData.email.trim()) {
        setError('❌ Email is required');
        return;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        setError('❌ Please enter a valid email address');
        return;
      }
      
      const updateData = {
        full_name: formData.full_name,
        email: formData.email,
        phone_no: formData.phone_no,
        student_id_number: formData.student_id_number,
        grade_id: formData.grade_id
      };
      
      await api.put(`/admin/students/${studentId}`, updateData);
      setSuccess('✅ Student updated successfully!');
      setEditMode(false);
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('❌ ' + (err.response?.data?.message || 'Failed to update student'));
    }
  };

  const handleCancel = () => {
    setEditMode(false);
    setFormData({
      full_name: student?.full_name || '',
      email: student?.email || '',
      phone_no: (student?.phone_number ?? student?.phone_no) || '',
      student_id_number: student?.student_id_number || '',
      grade_id: student?.grade_id || ''
    });
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div></div>;
  }

  if (error && !student) {
    return (
      <div className="space-y-6">
        <button onClick={() => navigate('/admin/students')} className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-medium">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">{error}</div>
      </div>
    );
  }

  if (!student) return <div>No student found</div>;

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <button onClick={() => navigate('/admin/students')} className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-medium">
        <ArrowLeft className="w-4 h-4" /> Back to Students
      </button>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">{error}</div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-green-700">{success}</div>
      )}

      {/* Student Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-indigo-100 flex items-center justify-center overflow-hidden">
              {student.avatar_thumb_path || student.avatar_path ? (
                <button onClick={() => setShowPreview(true)} className="w-full h-full p-0 m-0 block">
                  <img src={(student.avatar_thumb_path || student.avatar_path) ? getAvatarUrl(student.avatar_thumb_path || student.avatar_path) : ''} alt="avatar" className="w-full h-full object-cover" />
                </button>
              ) : (
                <span className="text-2xl font-bold text-indigo-600">{student.full_name?.charAt(0)}</span>
              )}

              {/* Preview modal */}
              {showPreview && (student.avatar_path || student.avatar_thumb_path) && (
                <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50" onClick={() => setShowPreview(false)}>
                  <div className="bg-white rounded-lg overflow-hidden shadow-lg max-w-3xl max-h-[90vh]">
                    <button className="absolute right-4 top-4 text-white bg-black bg-opacity-40 rounded-full p-2" onClick={(e) => { e.stopPropagation(); setShowPreview(false); }}>Close</button>
                    <img src={getAvatarUrl(student.avatar_path || student.avatar_thumb_path)} alt="avatar-large" className="max-w-full max-h-[80vh] object-contain" />
                  </div>
                </div>
              )}
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-800">{student.full_name}</h1>
              <p className="text-gray-500 mt-1">Student ID: {student.student_id_number || 'N/A'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setEditMode(!editMode)}
              type="button"
              className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 font-medium transition"
            >
              <Edit2 className="w-4 h-4" />
              {editMode ? 'Cancel' : 'Edit'}
            </button>
            <button
              type="button"
              onClick={() => showConfirmation({
                title: 'Delete Student',
                message: `Are you sure you want to delete ${student.full_name}? This action cannot be undone.`,
                confirmText: 'Delete',
                cancelText: 'Cancel',
                type: 'danger',
                onConfirm: async () => {
                  try {
                    const res = await api.delete(`/admin/students/${studentId}`);
                    if (res.data && res.data.success) {
                      // Navigate back to students list after successful deletion
                      navigate('/admin/students');
                    } else {
                      setError('Failed to delete student');
                    }
                  } catch (err) {
                    setError(err.response?.data?.message || 'Failed to delete student');
                  }
                }
              })}
              className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 font-medium transition"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22" />
              </svg>
              Delete
            </button>
          </div>
        </div>
      </div>

      {/* Attendance Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Sessions</p>
              <p className="text-3xl font-bold text-gray-800 mt-2">{attendance.length}</p>
            </div>
            <Calendar className="w-12 h-12 text-gray-200" />
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-green-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-600">Present</p>
              <p className="text-3xl font-bold text-green-600 mt-2">{attendance.filter(a => a.status === 'Present').length}</p>
              <p className="text-xs text-green-500 mt-1">{attendance.length > 0 ? ((attendance.filter(a => a.status === 'Present').length / attendance.length) * 100).toFixed(1) : 0}%</p>
            </div>
            <Check className="w-12 h-12 text-green-200" />
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-red-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-red-600">Absent</p>
              <p className="text-3xl font-bold text-red-600 mt-2">{attendance.filter(a => a.status === 'Absent').length}</p>
            </div>
            <X className="w-12 h-12 text-red-200" />
          </div>
        </div>
      </div>

      {/* Attendance Records */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="p-6 border-b border-gray-100">
          <h2 className="text-xl font-bold text-gray-800">Attendance Records</h2>
          <p className="text-gray-500 text-sm mt-1">{attendance.length} sessions recorded</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Subject</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Description</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {attendance.map((record) => (
                <tr key={record.record_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <BookOpen className="w-4 h-4 text-indigo-600" />
                      <span className="font-medium text-gray-800">{record.subject_name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-gray-600">{new Date(record.session_date).toLocaleDateString()}</td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      record.status === 'Present' 
                        ? 'bg-green-100 text-green-700' 
                        : record.status === 'Absent'
                        ? 'bg-red-100 text-red-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                      {record.status || 'Unmarked'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-gray-600 text-sm">{record.session_description || '-'}</td>
                </tr>
              ))}
              {attendance.length === 0 && (
                <tr>
                  <td colSpan="4" className="px-6 py-8 text-center text-gray-400">No attendance records yet</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Marks Records */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="p-6 border-b border-gray-100">
          <h2 className="text-xl font-bold text-gray-800">Exam Marks</h2>
          <p className="text-gray-500 text-sm mt-1">{marks.length} exams recorded</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Subject</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Exam</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Marks</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Percentage</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {marks.map((mark, idx) => (
                <tr key={idx} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <BookOpen className="w-4 h-4 text-indigo-600" />
                      <span className="font-medium text-gray-800">{mark.subject_name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 font-medium text-gray-800">{mark.exam_name}</td>
                  <td className="px-6 py-4 text-gray-600">{new Date(mark.exam_date).toLocaleDateString()}</td>
                  <td className="px-6 py-4"><span className="font-semibold text-gray-800">{mark.obtained_marks} / {mark.total_marks}</span></td>
                  <td className="px-6 py-4"><span className="text-sm text-gray-600">{mark.percentage}%</span></td>
                </tr>
              ))}
              {marks.length === 0 && (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-gray-400">No exam marks yet</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Student Details */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-lg font-bold text-gray-800 mb-6">Student Information</h2>

        {editMode ? (
          <div className="space-y-6">
            {/* Full Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
              <input
                type="text"
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            {/* Phone Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
              <input
                type="tel"
                name="phone_no"
                value={formData.phone_no}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            {/* Student ID */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Student ID Number</label>
              <input
                type="text"
                name="student_id_number"
                value={formData.student_id_number}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <button
                onClick={handleSave}
                className="inline-flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium transition"
              >
                <Check className="w-4 h-4" /> Save Changes
              </button>
              <button
                onClick={handleCancel}
                className="inline-flex items-center gap-2 px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium transition"
              >
                <X className="w-4 h-4" /> Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Display Mode */}
            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
              <Mail className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <p className="font-medium text-gray-800">{student.email}</p>
              </div>
            </div>

            {(student.phone_number || student.phone_no) && (
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                <Phone className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Phone Number</p>
                  <p className="font-medium text-gray-800">{student.phone_number ?? student.phone_no}</p>
                </div>
              </div>
            )}

            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
              <FileText className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Student ID Number</p>
                <p className="font-medium text-gray-800">{student.student_id_number || 'Not assigned'}</p>
              </div>
            </div>

            {student.grade_name && (
              <div className="flex items-center gap-4 p-4 bg-blue-50 rounded-lg">
                <BookOpen className="w-5 h-5 text-blue-400" />
                <div>
                  <p className="text-sm text-blue-600">Grade</p>
                  <p className="font-medium text-blue-800">{student.grade_name}</p>
                </div>
              </div>
            )}

            {student.is_active !== undefined && (
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                <User className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <p className={`font-medium ${Number(student.is_active) === 1 ? 'text-green-600' : 'text-red-600'}`}>
                    {Number(student.is_active) === 1 ? 'Active' : 'Inactive'}
                  </p>
                </div>
              </div>
            )}

            {student.created_at && (
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                <Calendar className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Created</p>
                  <p className="font-medium text-gray-800">
                    {new Date(student.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            )}

            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
              <Clock className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Last Login</p>
                <p className="font-medium text-gray-800">{formatDateTime(student.last_login)}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
